'use client';

import { Check, ChevronDown } from 'lucide-react';

import { memo, useCallback, useMemo, useState } from 'react';

import { SimpleDropdown, SimpleSearchInput } from '@/shared/components';
import { DropdownOption } from '@/shared/components/SimpleDropdown';

import { cn } from '@/lib/utils';

import { useDashboardFilters } from '../../hooks/use-dashboard-filters';

interface FormFiltersProps {
	className?: string;
}

function FormFilters({ className }: FormFiltersProps) {
	const { filters, updateFilters } = useDashboardFilters();

	const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

	const handleFilterChange = useCallback(
		(filterKey: string) => {
			return () => {
				if (filterKey === 'allForms') {
					updateFilters({ liveForms: false, closedForms: false, createdByMe: false });
				} else {
					updateFilters({
						liveForms: false,
						closedForms: false,
						createdByMe: false,
						[filterKey]: !filters[filterKey as keyof typeof filters],
					});
				}
			};
		},
		[updateFilters, filters]
	);

	const handleSearch = useCallback(
		(value: string) => {
			updateFilters({ search: value });
		},
		[updateFilters]
	);

	const filterOptions = useMemo(
		() =>
			[
				{
					label: 'All forms',
					value: 'allForms',
					onClick: handleFilterChange('allForms'),
					icon:
						!filters.liveForms && !filters.closedForms && !filters.createdByMe ? (
							<Check className="size-4" />
						) : (
							<div className="size-4" />
						),
				},
				{
					label: 'Live forms',
					value: 'liveForms',
					onClick: handleFilterChange('liveForms'),
					icon: filters.liveForms ? <Check className="size-4" /> : <div className="size-4" />,
				},
				{
					label: 'Closed forms',
					value: 'closedForms',
					onClick: handleFilterChange('closedForms'),
					icon: filters.closedForms ? <Check className="size-4" /> : <div className="size-4" />,
				},
				{
					label: 'Created by me',
					value: 'createdByMe',
					onClick: handleFilterChange('createdByMe'),
					icon: filters.createdByMe ? <Check className="size-4" /> : <div className="size-4" />,
				},
			] as DropdownOption[],
		[handleFilterChange, filters]
	);

	const activeFilterValue = useMemo(() => {
		if (filters?.liveForms) return 'Live forms';
		if (filters?.closedForms) return 'Closed forms';
		if (filters?.createdByMe) return 'Created by me';
		return 'All forms';
	}, [filters]);

	return (
		<div className={cn('flex items-center justify-between gap-4 overflow-hidden', className)}>
			<SimpleDropdown
				open={isFilterDropdownOpen}
				onOpenChange={setIsFilterDropdownOpen}
				options={filterOptions}
				dropdownAlign="end"
				dropdownSide="bottom"
			>
			<span className="flex shrink-0 cursor-pointer items-center gap-2 text-base text-secondary hover:text-primary">
				{activeFilterValue}
				<ChevronDown
					className={cn('size-4 transition-transform', isFilterDropdownOpen ? 'rotate-180' : '')}
				/>
			</span>
			</SimpleDropdown>

			<SimpleSearchInput
				value={filters.search}
				onSearch={handleSearch}
				retractable
				alwaysExpandedIfFilled
				className="!p-0 h-8 max-w-[180px] shrink-0"
				inputClassName="!h-8 !border-transparent !bg-transparent !shadow-none hover:!bg-accent"
				expandedInputClassName="!w-full !border-input !bg-background hover:!bg-background focus-visible:!ring-0 focus-visible:!border-input"
			/>
		</div>
	);
}

export default memo(FormFilters);
